import resetQuery from '@/utils/resetQuery';
import resetQueryPartial from '@/utils/resetQueryPartial';
import { fromJS } from 'immutable';
import { sendAnalytics } from '@/services/analyticsService';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import type { User } from '@/modules/user/userTypes';
import type { Email } from '../email/emailTypes';
import { EmailActionTypes, EmailAction } from '../email/emailTypes';
import {
	Business,
	BusinessQuery,
	ContactType,
	DashboardAction,
	DashboardActionTypes as actionTypes,
	List,
	Location,
	Activity
} from './dashboardTypes';

/// / We want the initial state to be based on the user object
const initialState = fromJS({
	query: {
		minDistance: 0,
		maxDistance: 40,
		limit: 50,
		skip: 0,
		sortField: 'score',
		sort: { score: '-1' },
		count: true,
		searches: []
	},
	locations: {
		data: [],
		failure: '',
		isLoading: false,
		selected: {}
	},
	businesses: {
		data: [],
		allData: [],
		allDataLoading: false,
		count: 0,
		failure: '',
		isLoading: false,
		selected: {
			notes: {},
			threadedEmails: []
		},
		communicationCenter: {
			data: [],
			count: 0
		},
		addedDataSuccess: false,
		deletedDataSuccess: false
	},
	lists: {
		data: [],
		failure: '',
		isLoading: false,
		selected: {},
		smartLists: {
			data: {},
			loading: false,
			success: false,
			failure: '',
			selected: false,
			remaining: 5
		}
	},
	notes: {
		isLoading: false,
		failure: ''
	},
	googlePlaces: {
		data: [],
		failure: false,
		loading: false
	},
	requestNewLocation: {
		success: false,
		failure: false
	},
	requestAddLocationFeature: {
		success: false,
		failure: false
	},
	activities: {
		data: [],
		sortedByList: [],
		searched: [],
		searchedByList: [],
		isLoading: false,
		failure: '',
		emails: {
			isLoading: {},
			data: {},
			failure: {}
		}
	},
	activityNotes: {
		isLoading: false,
		success: null,
		failure: ''
	}
});

export default (
	state = initialState,
	action: DashboardAction | EmailAction
) => {
	switch (action.type) {
		case actionTypes.DASHBOARD_SET_QUERY: {
			const query = fromJS(action.payload ?? {}).toJS();

			if (
				!query.sort
			) {
				/// / Ensure sort is always defined in the query
				query.sort = { score: -1 };
				query.sortField = 'score';
			}
			if (query.user) {
				delete query.user;
			}
			const includes =
				query?.searches
					?.filter((s: any) => s.type === 0)
					.map((s: any) => s.value) || [];
			const excludes =
				query?.searches
					?.filter((s: any) => s.type === 1)
					.map((s: any) => s.value) || [];

			if (includes.length) {
				query.includes = includes;
			}
			if (excludes.length) {
				query.excludes = excludes;
			}

			const locationSelected = state
				.getIn(['locations', 'selected'])
				.toJS();

			if (locationSelected?.radius) {
				query.radius = locationSelected?.radius;
			}

			return state.set('query', fromJS(query));
		}
		case actionTypes.DASHBOARD_RESET_QUERY: {
			const locationSelected = state
				.getIn(['locations', 'selected'])
				.toJS();
			const listSelected = state.getIn(['lists', 'selected']).toJS();
			const query: BusinessQuery = state.get('query').toJS();

			if (query.user) {
				delete query.user;
			}

			return state.set(
				'query',
				fromJS(resetQuery(query, locationSelected, listSelected))
			);
		}
		case actionTypes.DASHBOARD_RESET_QUERY_PARTIAL: {
			const query: BusinessQuery = state.get('query').toJS();

			return state.set(
				'query',
				fromJS(resetQueryPartial(query, action.payload))
			);
		}
		case actionTypes.DASHBOARD_SELECT_LOCATION: {
			const queryNew = state.get('query').toJS();

			if (
				!queryNew.sort
			) {
				// Ensure sort is always defined in the query
				queryNew.sort = { score: -1 };
				queryNew.sortField = 'score';
			}

			// remove user from the query to avoid sending it to the backend
			if (queryNew.user) {
				delete queryNew.user;
			}

			const listSelected = state.getIn(['lists', 'selected']).toJS();

			if (Number(action.payload) === -1 && !listSelected?.businessIds) {
				delete queryNew.businessIds;

				delete queryNew.coordinates;
			}

			const locations = state.getIn(['locations', 'data']).toJS();

			const location = locations.find(
				(value: Location) => String(value.id) === String(action.payload)
			);

			const businesses = state.getIn(['businesses', 'data']);
			const count = state.getIn(['businesses', 'count']);

			return state
				.setIn(['locations', 'selected'], fromJS(location ?? {}))
				.setIn(
					['businesses', 'data'],
					!location ? fromJS([]) : businesses
				)
				.setIn(['businesses', 'count'], !location ? 0 : count)
				.set('query', fromJS(queryNew));
		}
		case actionTypes.DASHBOARD_SELECT_BUSINESS: {
			if (!action.payload)
				return state.setIn(['businesses', 'selected'], fromJS({}));

			if (Object.keys(action.payload).length > 0) {
				const businessesAnalyzed = localStorage.getItem(
					'businesses_analyzed'
				);

				if (!businessesAnalyzed) {
					localStorage.setItem(
						'businesses_analyzed',
						JSON.stringify([action.payload._id])
					);
				} else {
					const businessesAnalyzedArray =
						JSON.parse(businessesAnalyzed);

					if (Array.isArray(businessesAnalyzedArray)) {
						const alreadyAnalyzed = businessesAnalyzedArray.find(
							businessId => businessId === action.payload._id
						);

						if (!alreadyAnalyzed) {
							businessesAnalyzedArray.push(action.payload._id);

							localStorage.setItem(
								'businesses_analyzed',
								JSON.stringify(businessesAnalyzedArray)
							);
						}
					}
				}
			}

			return state.setIn(
				['businesses', 'selected'],
				fromJS(action.payload)
			);
		}
		case actionTypes.DASHBOARD_UNSELECT_BUSINESSES:
			return state.setIn(['businesses', 'selected'], fromJS({}));
		case actionTypes.DASHBOARD_GET_BUSINESSES_REQUEST:
			return state.setIn(['businesses', 'isLoading'], true);
		case actionTypes.DASHBOARD_GET_BUSINESSES_SUCCESS: {
			const {
				data: { result, count, cursor }
			} = action.payload;

			const countPrev = state.getIn(['businesses', 'count']);

			// Calculate the current page number based on `skip` and `limit`
			const currentPage =
				Math.floor(
					state.getIn(['query', 'skip']) /
						state.getIn(['query', 'limit'])
				) + 1;
			const serializedCursor = JSON.stringify(cursor);

			// Access existing cursors and update it as a dictionary
			const updatedCursors = state
				.getIn(['businesses', 'cursors'], fromJS({}))
				.set(currentPage, serializedCursor);

			return state
				.setIn(['businesses', 'count'], count ?? countPrev)
				.setIn(
					['businesses', 'data'],
					result ? fromJS(result) : fromJS([])
				)
				.setIn(['businesses', 'isLoading'], false)
				.setIn(['businesses', 'cursors'], updatedCursors); // Update the cursors in state
		}
		case actionTypes.DASHBOARD_GET_ALL_BUSINESSES_LOADING: {
			return state.setIn(['businesses', 'allDataLoading'], true);
		}
		case actionTypes.DASHBOARD_GET_ALL_BUSINESSES_SUCCESS: {
			const filteredBusinesses = action.payload.filter((b: Business) => {
				let businessEmail = '';
				let emailCount = 0;
				let lowPriorityCount = 0;

				if (b?.email && b?.email.length > 0) {
					businessEmail = String(b?.email).toLowerCase();
					emailCount++;

					if (b?.emailLowPriority) {
						lowPriorityCount++;
					}
				}

				if (b?.facebook?.email && b?.facebook?.email.length > 0) {
					businessEmail = String(b?.facebook?.email).toLowerCase();
					emailCount++;

					if (b?.facebook?.emailLowPriority) {
						lowPriorityCount++;
					}
				}

				if (b?.web?.email && b?.web?.email.length > 0) {
					businessEmail = String(b?.web?.email).toLowerCase();
					emailCount++;

					if (b?.web?.emailLowPriority) {
						lowPriorityCount++;
					}
				}

				if (b?.userAddedData && b?.userAddedData.length > 0) {
					for (let i = 0; i < b.userAddedData.length; i++) {
						if (b.userAddedData[i].type === 'email') {
							businessEmail = String(
								b.userAddedData[i].data
							).toLowerCase();
							emailCount++;

							if (b.userAddedData[i]?.emailLowPriority) {
								lowPriorityCount++;
							}
						}
					}
				}

				if (businessEmail.length === 0) {
					return false;
				}

				if (emailCount === lowPriorityCount) {
					return false;
				}

				return true;
			});

			return state
				.setIn(
					['businesses', 'allData'],
					filteredBusinesses ? fromJS(filteredBusinesses) : fromJS([])
				)
				.setIn(['businesses', 'allDataLoading'], false);
		}
		case actionTypes.DASHBOARD_SET_BUSINESSES_COUNT: {
			return state.setIn(['businesses', 'count'], action.payload);
		}
		case actionTypes.DASHBOARD_GET_BUSINESS_SUCCESS: {
			const data = action.payload;
			return state.setIn(['businesses', 'modalBusiness'], data);
		}
		case actionTypes.DASHBOARD_GET_SELECT_BUSINESS_SUCCESS: {
			const currentBusinessState = state
				.getIn(['businesses', 'selected'])
				.toJS();

			const data = action.payload;

			return state.setIn(
				['businesses', 'selected'],
				fromJS({ ...currentBusinessState, ...data })
			);
		}
		case actionTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_REQUEST: {
			const { notes } = action.payload;
			const currentNote = state.getIn(['businesses', 'selected', 'note']);
			const modalBiz = state.getIn(['businesses', 'modalBusiness']);
			if (
				(!modalBiz && notes === currentNote) ||
				(modalBiz && notes === modalBiz.note)
			) {
				return state;
			}
			return notes !== currentNote
				? state.setIn(['notes', 'isLoading'], true)
				: state;
		}
		case actionTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_SUCCESS: {
			const {
				data: {
					businessId,
					isContacted,
					contactType,
					lead,
					note,
					currentNoteText,
					userAddedData
				}
			} = action.payload;
			const modalBiz = state.getIn(['businesses', 'modalBusiness']);
			let stateNew = state;
			const businesses = state.getIn(['businesses', 'data']).toJS();
			const businessIndex = businesses.findIndex(
				(b: Business) => b._id === businessId
			);

			const contactedBusinesses = state.getIn([
				'businesses',
				'communicationCenter',
				'data'
			]);

			if (modalBiz) {
				const keys = Object.keys(action.payload);
				for (let i = 0; i < keys.length; i++) {
					modalBiz[keys[i]] = action.payload[keys[i]];
				}

				stateNew = state
					.setIn(['businesses', 'modalBusiness'], modalBiz)
					.setIn(
						['businesses', 'modalBusiness', 'note'],
						currentNoteText || note
					)
					.setIn(['notes', 'failure'], '');
			} else if (contactedBusinesses && contactedBusinesses.length > 0) {
				let contactedIndex;
				contactedBusinesses.forEach((b, ind) => {
					if (b.businessId === businessId) {
						contactedIndex = ind;
					}
				});
				stateNew = state
					.setIn(['notes', 'isLoading'], false)
					.setIn(['notes', 'failure'], '')
					.setIn(
						['businesses', 'selected', 'isContacted'],
						isContacted
					)
					.setIn(
						['businesses', 'selected', 'contactType'],
						fromJS(contactType)
					)
					.setIn(
						['businesses', 'selected', 'note'],
						currentNoteText || note
					)
					.setIn(['businesses', 'selected', 'lead'], lead)
					.setIn(
						['businesses', 'selected', 'userAddedData'],
						userAddedData
					)
					.setIn(['businesses', 'data', businessIndex, 'lead'], lead)
					.setIn(
						['businesses', 'data', businessIndex, 'userAddedData'],
						userAddedData
					)
					.setIn(
						['businesses', 'data', businessIndex, 'isContacted'],
						isContacted
					)
					.setIn(
						['businesses', 'data', businessIndex, 'contactType'],
						fromJS(contactType)
					)
					.setIn(['businesses', 'data', businessIndex, 'note'], note)
					.setIn(
						[
							'businesses',
							'communicationCenter',
							'data',
							contactedIndex,
							'note'
						],
						note
					)
					.setIn(
						[
							'businesses',
							'communicationCenter',
							'data',
							contactedIndex,
							'userAddedData'
						],
						userAddedData
					)
					.setIn(
						[
							'businesses',
							'communicationCenter',
							'data',
							contactedIndex,
							'lead'
						],
						lead
					)
					.setIn(
						[
							'businesses',
							'communicationCenter',
							'data',
							contactedIndex,
							'contactType'
						],
						contactType
					);
			} else {
				stateNew = state
					.setIn(['notes', 'isLoading'], false)
					.setIn(['notes', 'failure'], '')
					.setIn(
						['businesses', 'selected', 'isContacted'],
						isContacted
					)
					.setIn(
						['businesses', 'selected', 'contactType'],
						fromJS(contactType)
					)
					.setIn(
						['businesses', 'selected', 'note'],
						currentNoteText || note
					)
					.setIn(['businesses', 'selected', 'lead'], lead)
					.setIn(
						['businesses', 'selected', 'userAddedData'],
						userAddedData
					)
					.setIn(['businesses', 'data', businessIndex, 'lead'], lead)
					.setIn(
						['businesses', 'data', businessIndex, 'userAddedData'],
						userAddedData
					)
					.setIn(
						['businesses', 'data', businessIndex, 'isContacted'],
						isContacted
					)
					.setIn(
						['businesses', 'data', businessIndex, 'contactType'],
						fromJS(contactType)
					)
					.setIn(['businesses', 'data', businessIndex, 'note'], note);
			}
			return stateNew.setIn(['notes', 'failure'], '');
		}
		case actionTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_FAILURE: {
			const { message } = action.error!;
			return state
				.setIn(['notes', 'isLoading'], false)
				.setIn(['notes', 'failure'], message);
		}
		case actionTypes.DASHBOARD_SET_BUSINESS_REMINDER_FAILURE: {
			return state
				.setIn(['taskModal', 'isLoading'], false)
				.setIn(['taskModal', 'failure'], true)
				.setIn(['taskModal', 'success'], false);
		}
		case actionTypes.DASHBOARD_SET_BUSINESS_REMINDER_LOADING: {
			return state
				.setIn(['taskModal', 'isLoading'], true)
				.setIn(['taskModal', 'failure'], false)
				.setIn(['taskModal', 'success'], false);
		}
		case actionTypes.DASHBOARD_SET_BUSINESS_REMINDER_SUCCESS: {
			const data = state.getIn(['businesses', 'data']).toJS();
			let ind;
			for (let i = 0; i < data.length; i++) {
				if (data[i]._id === action.payload.businessId) {
					if (!data[i].reminder) {
						data[i].reminder = {};
					}
					data[i]['reminder']['hasReminderTaskSet'] = true;
					ind = i;
				}
			}
			if (
				ind &&
				data[ind] &&
				action.payload.note &&
				action.payload.note.reminder &&
				action.payload.note.reminder.reminderTriggerDate
			) {
				data[ind]['reminder']['reminderTriggerDate'] =
					action.payload.note.reminder.reminderTriggerDate;
				data[ind]['reminder']['reminderText'] =
					action.payload.note.reminder.reminderText;
			}
			return state
				.setIn(['taskModal', 'isLoading'], false)
				.setIn(['taskModal', 'failure'], false)
				.setIn(['taskModal', 'success'], true)
				.setIn(['businesses', 'data', ind], data[ind])
				.setIn(
					['businesses', 'selected', 'reminder', 'reminderText'],
					action.payload.note.reminder.reminderText
				)
				.setIn(
					[
						'businesses',
						'selected',
						'reminder',
						'hasReminderTaskSet'
					],
					true
				)
				.setIn(
					[
						'businesses',
						'selected',
						'reminder',
						'reminderTriggerDate'
					],
					action.payload.note.reminder.reminderTriggerDate
				);
		}
		case actionTypes.DASHBOARD_DELETE_BUSINESS_REMINDER_SUCCESS: {
			const data = state.getIn(['businesses', 'data']).toJS();
			let ind;
			for (let i = 0; i < data.length; i++) {
				if (data[i]._id === action.payload.businessId) {
					if (!data[i].reminder) {
						data[i].reminder = {};
					}
					data[i]['reminder']['hasReminderTaskSet'] = false;
					ind = i;
				}
			}
			return state
				.setIn(['taskModal', 'delete', 'success'], true)
				.setIn(
					[
						'businesses',
						'modalBusiness',
						'reminder',
						'hasReminderTaskSet'
					],
					false
				)
				.setIn(['businesses', 'data', ind], data[ind])
				.setIn(
					[
						'businesses',
						'selected',
						'reminder',
						'hasReminderTaskSet'
					],
					false
				);
		}
		case actionTypes.DASHBOARD_RESET_BUSINESS_REMINDER: {
			return state
				.setIn(['taskModal', 'isLoading'], false)
				.setIn(['taskModal', 'failure'], false)
				.setIn(['taskModal', 'success'], false);
		}
		case actionTypes.DASHBOARD_REMOVE_MODAL_BUSINESS: {
			return state.setIn(['businesses', 'modalBusiness'], undefined);
		}
		case actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_SUCCESS: {
			const { businessId, userEmail } = action.payload;
			const businesses = state.getIn(['businesses', 'data']).toJS();
			const allBusinesses = state.getIn(['businesses', 'allData']).toJS();

			const businessIndex = businesses.findIndex(
				(b: Business) => b._id === businessId
			);

			const allBusinessesIndex = allBusinesses.findIndex(
				(b: Business) => b._id === businessId
			);

			if (userEmail) {
				sendAnalytics({
					type: 'track',
					userId: userEmail,
					eventName: `User added data to a business.`,
					properties: {
						contactType: action.payload.data.slice(-1)[0].type,
						data: action.payload.data.slice(-1)[0].data
					}
				});
			}
			return state
				.setIn(
					['businesses', 'data', businessIndex, 'userAddedData'],
					action.payload.data
				)
				.setIn(
					[
						'businesses',
						'allData',
						allBusinessesIndex,
						'userAddedData'
					],
					action.payload.data
				)
				.setIn(
					['businesses', 'selected', 'userAddedData'],
					action.payload.data
				)
				.setIn(['businesses', 'addedDataLoading'], false)
				.setIn(['businesses', 'addedDataSuccess'], true)
				.setIn(['businesses', 'addedDataFailure'], false);
		}
		case actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_FAILURE: {
			return state
				.setIn(['businesses', 'addedDataLoading'], false)
				.setIn(['businesses', 'addedDataSuccess'], false)
				.setIn(['businesses', 'addedDataFailure'], true);
		}
		case actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_LOADING: {
			return state
				.setIn(['businesses', 'addedDataLoading'], true)
				.setIn(['businesses', 'addedDataSuccess'], false)
				.setIn(['businesses', 'addedDataFailure'], false);
		}
		case actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_RESET: {
			return state
				.setIn(['businesses', 'addedDataSuccess'], false)
				.setIn(['businesses', 'addedDataFailure'], false);
		}
		case actionTypes.DASHBOARD_DELETE_BUSINESSES_DATA: {
			return state.setIn(['businesses', 'deletedDataSuccess'], false);
		}
		case actionTypes.DASHBOARD_DELETE_BUSINESSES_DATA_SUCCESS: {
			const { businessId } = action.payload;
			const businesses = state.getIn(['businesses', 'data']).toJS();
			const allBusinesses = state.getIn(['businesses', 'allData']).toJS();

			const businessIndex = businesses.findIndex(
				(b: Business) => b._id === businessId
			);

			const allBusinessesIndex = allBusinesses.findIndex(
				(b: Business) => b._id === businessId
			);

			return state
				.setIn(
					['businesses', 'data', businessIndex, 'userAddedData'],
					action.payload.data.userAddedData
				)
				.setIn(
					[
						'businesses',
						'allData',
						allBusinessesIndex,
						'userAddedData'
					],
					action.payload.data.userAddedData
				)
				.setIn(
					['businesses', 'selected', 'userAddedData'],
					action.payload.data.userAddedData
				)
				.setIn(['businesses', 'deletedDataSuccess'], true);
		}

		case actionTypes.DASHBOARD_GET_MAPBOX_KEY_SUCCESS: {
			const { mapBoxApi } = action.payload;
			return state.setIn(['map', 'key'], mapBoxApi);
		}

		case actionTypes.DASHBOARD_GET_SELECTED_LIST: {
			return state.getIn(['list', 'selected']).toJS();
		}

		case actionTypes.DASHBOARD_SET_SELECTED_LIST: {
			return state.setIn(['list', 'selected'], action.payload);
		}

		case actionTypes.DASHBOARD_SELECT_LIST: {
			const queryNew = state.get('query').toJS();

			const locationSelected = state
				.getIn(['locations', 'selected'])
				.toJS();

			if (!locationSelected?.location && Number(action.payload) === -1) {
				delete queryNew.businessIds;

				delete queryNew.coordinates;
			}

			const lists = state.getIn(['lists', 'data']).toJS();

			const list = lists.find(
				(value: List) => String(value.id) === String(action.payload)
			);

			const businesses = state.getIn(['businesses', 'data']);
			const count = state.getIn(['businesses', 'count']);

			return state
				.setIn(['lists', 'selected'], fromJS(list ?? {}))
				.setIn(['businesses', 'data'], !list ? fromJS([]) : businesses)
				.setIn(['businesses', 'count'], !list ? 0 : count)
				.set('query', fromJS(queryNew));
		}
		case actionTypes.DASHBOARD_GET_LISTS_REQUEST:
			return state.setIn(['lists', 'isLoading'], true);
		case actionTypes.DASHBOARD_GET_LISTS_SUCCESS: {
			const { data } = action.payload;

			return state
				.setIn(['lists', 'data'], fromJS(data))
				.setIn(['lists', 'isLoading'], false);
		}
		case actionTypes.DASHBOARD_GET_SUBORDINATE_LISTS_REQUEST:
			return state.setIn(['subordinateLists', 'isLoading'], true);
		case actionTypes.DASHBOARD_GET_SUBORDINATE_LISTS_SUCCESS: {
			const { data } = action.payload;

			return state
				.setIn(['subordinateLists', 'data'], fromJS(data))
				.setIn(['subordinateLists', 'isLoading'], false);
		}
		case actionTypes.DASHBOARD_ADD_LIST_LOADING: {
			return state
				.setIn(['lists', 'new', 'loading'], true)
				.setIn(['lists', 'new', 'failure'], false)
				.setIn(['lists', 'new', 'success'], false);
		}
		case actionTypes.DASHBOARD_ADD_LIST_STALE: {
			return state
				.setIn(['lists', 'new', 'loading'], false)
				.setIn(['lists', 'new', 'failure'], undefined)
				.setIn(['lists', 'new', 'success'], undefined);
		}
		case actionTypes.DASHBOARD_ADD_LIST_SUCCESS: {
			const lists = state.getIn(['lists', 'data']).toJS();

			lists.unshift(action.payload);

			return state
				.setIn(['lists', 'data'], fromJS(lists))
				.setIn(['lists', 'new', 'loading'], false)
				.setIn(['lists', 'new', 'failure'], false)
				.setIn(['lists', 'new', 'success'], true);
		}

		case actionTypes.DASHBOARD_EDIT_LIST_SUCCESS: {
			const lists = state.getIn(['lists', 'data']).toJS();
			const newLists = lists.map((list: List) =>
				action.payload.id === list.id ? action.payload : list
			);

			return state
				.setIn(['lists', 'selected'], fromJS(action.payload))
				.setIn(['lists', 'data'], fromJS(newLists));
		}

		case actionTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_SUCCESS: {
			const { data } = action.payload;

			const lists = state.getIn(['lists', 'data']).toJS();

			const listSelected = state.getIn(['lists', 'selected']).toJS();

			const businesses = state.getIn(['businesses', 'data']).toJS();

			const stateNew = state
				.setIn(
					[
						'lists',
						'data',
						lists.findIndex((list: List) => list.id === data.id)
					],
					data
				)
				.setIn(['lists', 'new', 'loading'], false)
				.setIn(['lists', 'new', 'failure'], false)
				.setIn(['lists', 'new', 'success'], true);

			return Object.keys(listSelected).length > 0
				? stateNew
						.setIn(
							['lists', 'selected', 'businessIds'],
							data.businessIds
						)
						.setIn(
							['businesses', 'data'],
							listSelected?.businessIds &&
								listSelected.businessIds.length > 0
								? fromJS(
										businesses.filter((b: Business) =>
											data.businessIds.includes(b._id)
										)
									)
								: fromJS(businesses)
						)
				: stateNew;
		}
		case actionTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_SUCCESS: {
			const { data } = action.payload;
			const lists = state.getIn(['lists', 'data']).toJS();
			const listSelected = state.getIn(['lists', 'selected']).toJS();
			const businesses = state.getIn(['businesses', 'data']).toJS();
			const businessesCount = state.getIn(['businesses', 'count']);
			const allBusinesses = state.getIn(['businesses', 'allData']).toJS();

			// Filter out the removed business from allData
			const updatedAllBusinesses = allBusinesses.filter((b: Business) =>
				data.businessIds.includes(b._id)
			);

			const stateNew = state
				.setIn(
					[
						'lists',
						'data',
						lists.findIndex((list: List) => list.id === data.id)
					],
					data
				)
				.setIn(['businesses', 'count'], businessesCount - 1)
				.setIn(['businesses', 'allData'], fromJS(updatedAllBusinesses));

			return Object.keys(listSelected).length > 0
				? stateNew
						.setIn(
							['lists', 'selected', 'businessIds'],
							data.businessIds
						)
						.setIn(
							['businesses', 'data'],
							listSelected?.businessIds &&
								listSelected.businessIds.length > 0
								? fromJS(
										businesses.filter((b: Business) =>
											data.businessIds.includes(b._id)
										)
									)
								: fromJS(businesses)
						)
				: stateNew;
		}
		case actionTypes.DASHBOARD_REMOVE_LIST_SUCCESS: {
			const { id } = action.payload;
			const queryNew: BusinessQuery = state.get('query').toJS();

			const lists = state.getIn(['lists', 'data']).toJS();

			delete queryNew.listId;

			return state
				.setIn(
					['lists', 'data'],
					fromJS(
						lists.filter(
							(list: List) =>
								list?.id?.trim() !== String(id).trim()
						)
					)
				)
				.setIn(['lists', 'selected'], fromJS({}))
				.setIn(['businesses', 'data'], fromJS([]))
				.set('query', fromJS(queryNew));
		}
		case actionTypes.DASHBOARD_REMOVE_BATCH_LISTS_SUCCESS: {
			const { ids } = action.payload;

			const queryNew: BusinessQuery = state.get('query').toJS();

			const lists = state.getIn(['lists', 'data']).toJS();

			const isListIdInQuery = ids.find(
				(id: string) => queryNew.listId === id
			);
			if (isListIdInQuery) {
				delete queryNew.listId;
			}

			const filteredList = lists.filter(
				(list: List) => !ids.includes(list?.id?.trim())
			);

			return state
				.setIn(['lists', 'data'], fromJS(filteredList))
				.setIn(['lists', 'selected'], fromJS({}))
				.setIn(['businesses', 'data'], fromJS([]))
				.set('query', fromJS(queryNew));
		}
		case actionTypes.SET_SENT_EMAILS_IN_BUSINESSES: {
			const emails: Email[] = action.payload;
			const businesses = state.getIn(['businesses', 'data']).toJS();
			const selectedBusiness = state
				.getIn(['businesses', 'selected'])
				.toJS();
			selectedBusiness['nylasThreadId'] = emails[0].nylasThreadId;
			const getEmailType = (
				email: string,
				business: Business
			): string => {
				if (
					action.payload[0].userAddedData &&
					action.payload[0].userAddedData.exists
				) {
					return 'userAddedEmail';
				}
				const primaryEmail = business?.facebook?.email
					? String(business?.facebook?.email).toLowerCase()
					: '';

				const secondaryEmail = business?.email
					? String(business?.email).toLowerCase()
					: '';

				const thirdEmail = business?.web?.email
					? String(business?.web?.email).toLowerCase()
					: '';

				if (email === primaryEmail) {
					return 'emailPrimary';
				}

				if (email === secondaryEmail) {
					return 'emailSecondary';
				}

				if (email === thirdEmail) {
					return 'thirdEmail';
				}
				return '';
			};

			const newBusinesses = businesses.map((business: Business) => {
				const newBusiness: Business = fromJS(business).toJS();
				if (
					newBusiness._id === emails[0].businessId &&
					emails[0].nylasThreadId
				) {
					newBusiness['nylasThreadId'] = emails[0].nylasThreadId;
				}

				const email = emails.find(
					({ businessId }: Email) => businessId === newBusiness._id
				);

				if (email) {
					newBusiness.emailStatus = String(
						email?.mj_status
					).trim() as string;
					newBusiness.emailStatusCount = Number(
						String(email?.mj_status_count).trim()
					);
					newBusiness.emailSubject = email?.subject;
					newBusiness.emailBody = email?.body;

					if (!email?.isScheduledEmail) {
						newBusiness.isContacted = true;

						const emailType = getEmailType(
							email.email as string,
							newBusiness
						);

						if (emailType === 'userAddedEmail') {
							newBusiness.userAddedData[
								action.payload[0].userAddedData.index
							].checked = true;
						} else if (newBusiness.contactType) {
							newBusiness.contactType[
								emailType as keyof ContactType
							] = true;
						} else {
							newBusiness.contactType = {
								[emailType]: true
							};
						}
					}
				}

				return newBusiness;
			});

			if (selectedBusiness._id) {
				const email = emails.find(
					({ businessId }: Email) =>
						businessId === selectedBusiness._id
				);

				selectedBusiness.emailStatus = String(
					email?.mj_status
				).trim() as string;
				selectedBusiness.emailStatusCount = Number(
					String(email?.mj_status_count).trim()
				);
				selectedBusiness.emailSubject = email?.subject;
				selectedBusiness.emailBody = email?.body;

				if (!email?.isScheduledEmail) {
					selectedBusiness.isContacted = true;

					const emailType = getEmailType(
						email?.email as string,
						selectedBusiness
					);

					if (emailType === 'userAddedEmail') {
						selectedBusiness.userAddedData[
							action.payload[0].userAddedData.index
						].checked = true;
					} else if (selectedBusiness.contactType) {
						selectedBusiness.contactType[
							emailType as keyof ContactType
						] = true;
					} else {
						selectedBusiness.contactType = {
							[emailType]: true
						};
					}
				}
			}

			return state
				.setIn(['businesses', 'data'], fromJS(newBusinesses))
				.setIn(['businesses', 'selected'], fromJS(selectedBusiness));
		}
		case actionTypes.EMAIL_GET_EMAIL_THREAD_LOADING: {
			return state.setIn(
				['businesses', 'selected', 'threadedEmailsLoading'],
				true
			);
		}
		case actionTypes.EMAIL_GET_EMAIL_THREAD_SUCCESS: {
			const { thread, businessId } = action.payload;

			const contactedBusinesses = state.getIn([
				'businesses',
				'communicationCenter',
				'data'
			]);

			if (contactedBusinesses && contactedBusinesses?.length > 0) {
				const businessesFiltered = contactedBusinesses.map(b => {
					if (b.businessId == businessId) {
						b.emailIsRead = true;
					}
					return b;
				});

				state.setIn(
					[['businesses', 'communicationCenter', 'data']],
					businessesFiltered
				);
			}

			return state
				.setIn(['businesses', 'selected', 'threadedEmails'], thread)
				.setIn(
					['businesses', 'selected', 'threadedEmailsLoading'],
					false
				);
		}
		case actionTypes.EMAIL_GET_EMAIL_THREAD_FAILURE: {
			return state.setIn(
				['businesses', 'selected', 'threadedEmailsLoading'],
				false
			);
		}
		case actionTypes.DASHBOARD_EXPORT_BUSINESSES_SUCCESS:
			return state.removeIn(['query', 'queryType']);
		case actionTypes.DASHBOARD_ADD_ONE_TO_SAVE_LIST: {
			const business = action.payload;

			let list = [];
			const currentList = state.getIn(['lists', 'selectedBusinesses']);
			let block = false;
			if (currentList && currentList.length > 0) {
				for (let i = 0; i < currentList.length; i++) {
					if (currentList[i]._id === business._id) {
						block = true;
					}
				}
				list = [...currentList];
			} else {
				list = [];
			}

			if (!block) {
				list.push(business);
				return state.setIn(['lists', 'selectedBusinesses'], list);
			}
			return state;
		}
		case actionTypes.DASHBOARD_ADD_MANY_TO_SAVE_LIST: {
			const businessList = action.payload;

			let list = [];
			const currentList = state.getIn(['lists', 'selectedBusinesses']);

			for (let i = 0; i < businessList.length; i++) {
				let addToList = true;
				for (let x = 0; x < currentList.length; x++) {
					if (businessList[i]._id === currentList[x]._id) {
						addToList = false;
					}
				}
				if (addToList) {
					list.push(businessList[i]);
				}
			}
			list = [...list, ...currentList];
			return state.setIn(['lists', 'selectedBusinesses'], list);
		}
		case actionTypes.DASHBOARD_REMOVE_ONE_FROM_SAVE_LIST: {
			const business = action.payload;
			const currentList = state.getIn(['lists', 'selectedBusinesses']);
			if (currentList && currentList.length > 0) {
				for (let i = 0; i < currentList.length; i++) {
					if (currentList[i]._id === business._id) {
						currentList.splice(i, 1);
					}
				}
			}
			const list = [...currentList];
			return state.setIn(['lists', 'selectedBusinesses'], list);
		}
		case actionTypes.DASHBOARD_REMOVE_MANY_FROM_SAVE_LIST: {
			const businesses = action.payload;
			const currentList = state.getIn(['lists', 'selectedBusinesses']);
			const list = [];

			for (let i = 0; i < currentList.length; i++) {
				let addToList = true;
				for (let x = 0; x < businesses.length; x++) {
					if (currentList[i]._id === businesses[x]._id) {
						addToList = false;
					}
				}
				if (addToList) {
					list.push(currentList[i]);
				}
			}
			return state.setIn(['lists', 'selectedBusinesses'], list);
		}
		case actionTypes.DASHBOARD_REMOVE_ALL_FROM_SAVE_LIST: {
			return state.setIn(['lists', 'selectedBusinesses'], []);
		}
		case actionTypes.DASHBOARD_CLOSE_BUSINESS_PREVIEW: {
			return state.setIn(['businesses', 'closePreview'], action.payload);
		}
		case actionTypes.DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES_SUCCESS: {
			return state
				.setIn(
					['businesses', 'communicationCenter', 'data'],
					action.payload.data.result
				)
				.setIn(
					['businesses', 'communicationCenter', 'count'],
					action.payload.data.count
				)
				.setIn(['lists', 'selected'], fromJS({}));
		}
		case actionTypes.DASHBOARD_GET_BUSINESS_OPEN_NOTE: {
			return state.setIn(['businesses', 'openToNote'], true);
		}
		case actionTypes.DASHBOARD_GET_BUSINESS_CLOSE_NOTE: {
			return state.setIn(['businesses', 'openToNote'], false);
		}
		case actionTypes.DASHBOARD_OPEN_EMAIL_PREVIEW: {
			return state.setIn(['businesses', 'openEmailPreview'], true);
		}
		case actionTypes.DASHBOARD_OPEN_EMAIL_PREVIEW_CLEAR: {
			return state.setIn(['businesses', 'openEmailPreview'], false);
		}
		case actionTypes.DASHBOARD_GET_GOOGLE_PLACES: {
			return state
				.setIn(['googlePlaces', 'loading'], true)
				.setIn(['googlePlaces', 'data'], fromJS([]));
		}
		case actionTypes.DASHBOARD_GET_GOOGLE_PLACES_SUCCESS: {
			const shortenedArray = action.payload.slice(0, 5);

			return state
				.setIn(['googlePlaces', 'loading'], false)
				.setIn(['googlePlaces', 'failure'], false)
				.setIn(['googlePlaces', 'data'], fromJS(shortenedArray));
		}
		case actionTypes.DASHBOARD_GET_GOOGLE_PLACES_FAILURE: {
			return state
				.setIn(['googlePlaces', 'loading'], false)
				.setIn(['googlePlaces', 'failure'], true)
				.setIn(['googlePlaces', 'data'], fromJS([]));
		}
		case actionTypes.DASHBOARD_CLEAR_GOOGLE_PLACES: {
			return state
				.setIn(['googlePlaces', 'loading'], false)
				.setIn(['googlePlaces', 'data'], fromJS([]));
		}
		case actionTypes.DASHBOARD_REQUEST_NEW_LOCATION_SUCCESS: {
			return state
				.setIn(['requestNewLocation', 'success'], true)
				.setIn(['requestNewLocation', 'failure'], false);
		}
		case actionTypes.DASHBOARD_REQUEST_NEW_LOCATION_FAILURE: {
			return state
				.setIn(['requestNewLocation', 'success'], false)
				.setIn(['requestNewLocation', 'failure'], true);
		}
		case actionTypes.DASHBOARD_REQUEST_NEW_LOCATION_CLEAR_STATUS: {
			return state
				.setIn(['requestNewLocation', 'loading'], false)
				.setIn(['requestNewLocation', 'success'], false)
				.setIn(['requestNewLocation', 'failure'], false);
		}
		case actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_SUCCESS: {
			return state
				.setIn(['requestAddLocationFeature', 'success'], true)
				.setIn(['requestAddLocationFeature', 'failure'], false);
		}
		case actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_FAILURE: {
			return state
				.setIn(['requestAddLocationFeature', 'success'], false)
				.setIn(['requestAddLocationFeature', 'failure'], true);
		}
		case actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_CLEAR_STATUS: {
			return state
				.setIn(['requestAddLocationFeature', 'loading'], false)
				.setIn(['requestAddLocationFeature', 'success'], false)
				.setIn(['requestAddLocationFeature', 'failure'], false);
		}
		case actionTypes.DASHBOARD_GET_ACTIVITIES_LOADING: {
			return state.setIn(['activities', 'isLoading'], true);
		}
		case actionTypes.DASHBOARD_GET_ACTIVITIES_SUCCESS: {
			const lists = state.getIn(['lists', 'data']).toJS();
			const activities = action.payload;
			const activitiesByList = {};
			activities.forEach((activity: Activity) => {
				if (
					activity.type === 'leadStatusChange' &&
					activity?.data?.status
				) {
					activity.header = 'You marked as a Lead';
				} else if (
					activity.type === 'leadStatusChange' &&
					activity?.data?.status === false
				) {
					activity.header = 'You marked as Not Lead';
				} else if (
					activity.type === 'leadStatusChange' &&
					activity?.data?.status === undefined
				) {
					activity.header = 'You removed Lead status';
				} else if (activity.type === 'emailReplied') {
					activity.header = 'Business replied via email';
				} else if (activity.type === 'emailSent') {
					activity.header = 'You emailed business';
				} else if (activity.type === 'emailQueued') {
					activity.header = 'You sent an email into queue';
				} else if (activity.type === 'emailFailed') {
					activity.header = 'You sent an email that failed';
				} else if (activity.type === 'note') {
					activity.header = 'You added a Note';
				} else if (activity.type === 'phoneContacted') {
					activity.header = 'You called business';
				} else if (activity.type === 'messengerContacted') {
					activity.header = 'You messaged business';
				} else if (activity.type === 'instagramMessengerContacted') {
					activity.header = 'You messaged business';
				} else if (activity.type === 'businessAddedToList') {
					activity.header = `You added to ${activity?.data?.listName ? activity?.data?.listName : 'a list'}`;
				} else if (activity.type === 'businessRemovedFromList') {
					activity.header = `You removed from ${activity?.data?.listName ? activity?.data?.listName : 'a list'}`;
				} else if (activity.type === 'emailOpened') {
					activity.header = 'Business opened email';
				} else if (activity.type === 'websiteOpened') {
					activity.header = "You opened business's website";
				} else if (activity.type === 'yelpOpened') {
					activity.header = "You opened business's Yelp page";
				} else if (activity.type === 'instagramOpened') {
					activity.header = "You opened business's Instagram";
				} else if (activity.type === 'pinterestOpened') {
					activity.header = "You opened business's Pinterest";
				} else if (activity.type === 'youtubeOpened') {
					activity.header = "You opened business's Youtube";
				} else if (activity.type === 'twitterOpened') {
					activity.header = "You opened business's Twitter";
				} else if (activity.type === 'tiktokOpened') {
					activity.header = "You opened business's TikTok";
				} else if (activity.type === 'snapchatOpened') {
					activity.header = "You opened business's Snapchat";
				} else if (activity.type === 'reminderCreated') {
					activity.header = 'You created Reminder';
				} else if (activity.type === 'reminderDeleted') {
					activity.header = 'You deleted Reminder';
				} else if (activity.type === 'customContactInfoCreated') {
					activity.header = 'You added Contact Data';
				} else if (activity.type === 'customContactInfoDeleted') {
					activity.header = 'You deleted Contact Data';
				} else if (activity.type === 'facebookOpened') {
					activity.header = "You opened business's Facebook page";
				} else if (activity.type === 'googleOpened') {
					activity.header = 'You Googled this business';
				} else if (activity.type === 'emailScheduled') {
					activity.header = 'You scheduled an email';
				} else if (activity.type === 'cancelledScheduledEmail') {
					activity.header = 'You cancelled a scheduled email';
				}

				lists.forEach((list: List) => {
					if (
						list.id === activity?.listId &&
						activitiesByList.hasOwnProperty(list.name)
					) {
						activitiesByList[list.name].push(activity);
					} else if (list.id === activity?.listId) {
						activitiesByList[list.name] = [activity];
					} else if (
						list.id === activity?.data?.listId &&
						activitiesByList.hasOwnProperty(list.name)
					) {
						activitiesByList[list.name].push(activity);
					} else if (list.id === activity?.data?.listId) {
						activitiesByList[list.name] = [activity];
					}
				});

				if (
					!activity?.listId &&
					!activity?.data?.listId &&
					activitiesByList.hasOwnProperty('General')
				) {
					activitiesByList['General'].push(activity);
				} else if (!activity?.listId && !activity?.data?.listId) {
					activitiesByList['General'] = [activity];
				}
			});

			const mostRecentActivitiesByList = Object.entries(
				activitiesByList
			).sort((a, b) => {
				return b[1][0].createdAt - a[1][0].createdAt;
			});

			return state
				.setIn(['activities', 'isLoading'], false)
				.setIn(['activities', 'data'], fromJS(activities))
				.setIn(
					['activities', 'sortedByList'],
					fromJS(mostRecentActivitiesByList)
				)
				.setIn(['activities', 'searched'], fromJS([]))
				.setIn(['activities', 'searchedByList'], fromJS([]));
		}
		case actionTypes.DASHBOARD_GET_ACTIVITIES_FAILURE: {
			return state
				.setIn(['activities', 'isLoading'], false)
				.setIn(['activities', 'failure'], action.error?.message);
		}
		case actionTypes.DASHBOARD_ACTIVITY_SEARCH: {
			const searchTerm = action.payload;
			const activities = state.getIn(['activities', 'data']).toJS();
			const searchedActivities = activities.filter(activity => {
				return (
					activity?.header
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					activity?.data?.text
						?.toLowerCase()
						.includes(searchTerm.toLowerCase())
				);
			});

			// If no activities match the search term, return the unchanged state
			if (searchedActivities.length === 0) {
				const updatedState = state
					.setIn(['activities', 'searched'], fromJS([]))
					.setIn(['activities', 'searchedByList'], fromJS([]));

				return updatedState;
			}

			const lists = state.getIn(['lists', 'data']).toJS();

			const searchedActivitiesByList = {};

			searchedActivities.forEach((activity: Activity) => {
				lists.forEach((list: List) => {
					if (
						list.id === activity.listId &&
						searchedActivitiesByList.hasOwnProperty(list.name)
					) {
						searchedActivitiesByList[list.name].push(activity);
					} else if (list.id === activity.listId) {
						searchedActivitiesByList[list.name] = [activity];
					}
				});

				if (
					!activity.listId &&
					searchedActivitiesByList.hasOwnProperty('General')
				) {
					searchedActivitiesByList['General'].push(activity);
				} else if (!activity.listId) {
					searchedActivitiesByList['General'] = [activity];
				}
			});

			if (searchedActivities.length === activities.length) {
				return state
					.setIn(['activities', 'searched'], fromJS([]))
					.setIn(['activities', 'searchedByList'], fromJS([]));
			}

			const mostRecentActivitiesByList = Object.entries(
				searchedActivitiesByList
			).sort((a, b) => {
				return b[1][0].createdAt - a[1][0].createdAt;
			});

			return state
				.setIn(['activities', 'searched'], fromJS(searchedActivities))
				.setIn(
					['activities', 'searchedByList'],
					fromJS(mostRecentActivitiesByList)
				);
		}
		case actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_LOADING: {
			return state.setIn(['activityNotes', 'isLoading'], true);
		}
		case actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_SUCCESS: {
			return state
				.setIn(['activityNotes', 'isLoading'], false)
				.setIn(['activityNotes', 'success'], true)
				.setIn(['activityNotes', 'failure'], '');
		}
		case actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_FAILURE: {
			return state
				.setIn(['activityNotes', 'isLoading'], false)
				.setIn(['activityNotes', 'success'], false)
				.setIn(['activityNotes', 'failure'], action.error?.message);
		}
		case actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_CLEAR: {
			return state
				.setIn(['activityNotes', 'isLoading'], false)
				.setIn(['activityNotes', 'success'], null)
				.setIn(['activityNotes', 'failure'], '');
		}
		case actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_LOADING: {
			const loading = { [action.payload.emailId]: true };
			return state.setIn(
				['activities', 'emails', 'isLoading'],
				fromJS(loading)
			);
		}
		case actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_SUCCESS: {
			const emails = state.getIn(['activities', 'emails', 'data']).toJS();
			const failure = state
				.getIn(['activities', 'emails', 'failure'])
				.toJS();
			const newEmails = {
				...emails,
				[action.payload.id]: action.payload
			};

			if (failure[action.payload.id]) {
				const newFailure = omit(failure, action.payload.id);
				return state
					.setIn(['activities', 'emails', 'isLoading'], fromJS({}))
					.setIn(['activities', 'emails', 'data'], fromJS(newEmails))
					.setIn(
						['activities', 'emails', 'failure'],
						fromJS(newFailure)
					);
			}

			return state
				.setIn(['activities', 'emails', 'isLoading'], fromJS({}))
				.setIn(['activities', 'emails', 'data'], fromJS(newEmails));
		}
		case actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_FAILURE: {
			const failure = state
				.getIn(['activities', 'emails', 'failure'])
				.toJS();
			const newFailure = {
				...failure,
				[action.error.emailId]: action.error.message
			};
			return state
				.setIn(['activities', 'emails', 'isLoading'], fromJS({}))
				.setIn(['activities', 'emails', 'failure'], fromJS(newFailure));
		}
		case actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_LOADING: {
			const loading = { [action.payload.emailId]: true };
			return state.setIn(
				['activities', 'emails', 'isLoading'],
				fromJS(loading)
			);
		}
		case actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_SUCCESS: {
			const emails = state.getIn(['activities', 'emails', 'data']).toJS();
			const failure = state
				.getIn(['activities', 'emails', 'failure'])
				.toJS();

			const parsedBody = action.payload.body.replace(/\n/g, '<br />');

			const newPayload = { ...action.payload, body: parsedBody };

			const newEmails = { ...emails, [action.payload.id]: newPayload };

			if (failure[action.payload.id]) {
				const newFailure = omit(failure, action.payload.id);
				return state
					.setIn(['activities', 'emails', 'isLoading'], fromJS({}))
					.setIn(['activities', 'emails', 'data'], fromJS(newEmails))
					.setIn(
						['activities', 'emails', 'failure'],
						fromJS(newFailure)
					);
			}

			return state
				.setIn(['activities', 'emails', 'isLoading'], fromJS({}))
				.setIn(['activities', 'emails', 'data'], fromJS(newEmails));
		}
		case actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_FAILURE: {
			const failure = state
				.getIn(['activities', 'emails', 'failure'])
				.toJS();
			const newFailure = {
				...failure,
				[action.error.emailId]: action.error.message
			};
			return state
				.setIn(['activities', 'emails', 'isLoading'], fromJS({}))
				.setIn(['activities', 'emails', 'failure'], fromJS(newFailure));
		}
		case actionTypes.DASHBOARD_CLEAR_ACTIVITY_DATA: {
			return state
				.setIn(['activities', 'data'], fromJS([]))
				.setIn(['activities', 'sortedByList'], fromJS([]))
				.setIn(['activities', 'searched'], fromJS([]))
				.setIn(['activities', 'searchedByList'], fromJS([]))
				.setIn(['activities', 'failure'], '')
				.setIn(['activities', 'emails', 'failure'], '')
				.setIn(['activities', 'emails', 'data'], fromJS({}));
		}
		case actionTypes.DASHBOARD_CLICK_DELETE_NOTE: {
			return state.setIn(['notes', 'clickDeleteNote'], true);
		}
		case actionTypes.DASHBOARD_CLICK_DELETE_NOTE_CLEAR: {
			return state.setIn(['notes', 'clickDeleteNote'], false);
		}
		case EmailActionTypes.EMAIL_CANCEL_SCHEDULED_EMAIL_SUCCESS: {
			const { businessId, data } = action.payload;

			if (!isEmpty(data)) {
				const businesses = state.getIn(['businesses', 'data']).toJS();
				const index = businesses.findIndex(
					(business: Business) => business._id === businessId
				);
				const newBusiness = {
					...businesses[index],
					emailStatus: data.emailStatus,
					emailStatusCount: data.emailStatusCount
				};
				businesses[index] = newBusiness;
				return state.setIn(['businesses', 'data'], fromJS(businesses));
			}
			return state;
		}
		case EmailActionTypes.EMAIL_GET_SCHEDULED_EMAILS_SUCCESS: {
			const data = action.payload;
			const count = action.payload.length;
			return state
				.setIn(['businesses', 'scheduledEmails', 'data'], data)
				.setIn(['businesses', 'scheduledEmails', 'count'], count);
		}
		case actionTypes.DASHBOARD_CLEAR_BUSINESSES: {
			return state.setIn(['businesses', 'data'], fromJS([]));
		}
		case actionTypes.DASHBOARD_CLEAR_CURSORS: {
			return state.setIn(['businesses', 'cursors'], fromJS({}));
		}
		case actionTypes.DASHBOARD_GET_BUSINESSES_FAILURE:
		case actionTypes.DASHBOARD_GET_LISTS_FAILURE:
		case actionTypes.DASHBOARD_ADD_LIST_REQUEST:
		case actionTypes.DASHBOARD_ADD_LIST_FAILURE:
		case actionTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_REQUEST:
		case actionTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_FAILURE:
		case actionTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_REQUEST:
		case actionTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_FAILURE:
		case actionTypes.DASHBOARD_REMOVE_LIST_REQUEST:
		case actionTypes.DASHBOARD_REMOVE_LIST_FAILURE:
		case actionTypes.DASHBOARD_REMOVE_BATCH_LISTS_REQUEST:
		case actionTypes.DASHBOARD_REMOVE_BATCH_LISTS_FAILURE:
		case actionTypes.DASHBOARD_EXPORT_BUSINESSES_REQUEST:
		case actionTypes.DASHBOARD_EXPORT_BUSINESSES_FAILURE:

		default:
			return state;
	}
};
